<template>
  <AdminComponent size="lg">
    <template #topbar>
      <h4 class="flex-grow-1 mb-0">Listing Imports</h4>
      <button class="btn btn-outline-primary" :disabled="loading" @click="loadImports">
        <i class="bi-arrow-clockwise me-1" aria-hidden="true" />
        {{ loading ? 'Refreshing…' : 'Refresh' }}
      </button>
    </template>

    <p class="text-muted">
      Latest successful import for each municipality. Discovered counts unique listings in that import;
      updates are existing listings refreshed, including those with unchanged details. New listings are first-time imports.
      Open History to inspect extract files, import results, and repeated checks of the same extract.
    </p>
    <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
    <div class="d-flex flex-wrap align-items-end gap-3 mb-3">
      <div class="flex-grow-1">
        <label for="import-municipality-search" class="form-label">Find a municipality</label>
        <input id="import-municipality-search" v-model="search" type="search" class="form-control"
               placeholder="Municipality or state" />
      </div>
      <div v-if="checkedAt" class="text-muted small" role="status">
        Refreshed {{ displayDate(checkedAt) }}
      </div>
    </div>
    <div v-if="loading && !checkedAt" class="text-center py-4" role="status">
      <span class="spinner-border spinner-border-sm me-2" aria-hidden="true" />Loading listing imports…
    </div>
    <div v-else-if="checkedAt" class="table-responsive" :aria-busy="loading">
      <table class="table align-middle">
        <caption>Import times use your local time zone. — means counts were not recorded.</caption>
        <thead>
          <tr>
            <th scope="col">Municipality</th>
            <th scope="col">Last successful import</th>
            <th scope="col" class="text-end">Discovered</th>
            <th scope="col" class="text-end">Updated</th>
            <th scope="col" class="text-end">New</th>
            <th scope="col">History</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="city in filteredMunicipalities" :key="city.cityId">
            <tr>
              <th scope="row" class="fw-normal">
                <router-link :to="{ name: 'admin-city-tab', params: { cityId: city.cityId, tabId: 'listing-services' } }">
                  {{ city.name }}{{ city.state ? `, ${city.state}` : '' }}
                </router-link>
                <div v-if="city.paused || !city.extractEnabled" class="small text-muted mt-1">
                  {{ city.paused ? 'Municipality paused' : 'Extract imports disabled' }}
                </div>
              </th>
              <td>
                <template v-if="city.lastImport">
                  <time v-if="city.lastImport.completedAt" :datetime="city.lastImport.completedAt">
                    {{ displayDate(city.lastImport.completedAt) }}
                  </time>
                  <span v-else class="text-muted">Time not recorded</span>
                  <div class="small text-muted">{{ importDescription(city.lastImport) }}</div>
                </template>
                <span v-else class="text-muted">No recorded import</span>
              </td>
              <td class="text-end import-count">{{ displayCount(city.lastImport?.discovered) }}</td>
              <td class="text-end import-count">{{ displayCount(city.lastImport?.updated) }}</td>
              <td class="text-end import-count">{{ displayCount(city.lastImport?.created) }}</td>
              <td>
                <button class="btn btn-sm btn-outline-primary" :aria-expanded="historyCityId === city.cityId"
                        :aria-controls="`import-history-${city.cityId}`" :aria-label="`Import history for ${city.name}`"
                        @click="historyCityId = historyCityId === city.cityId ? null : city.cityId">
                  {{ historyCityId === city.cityId ? 'Hide history' : 'History' }}
                </button>
              </td>
            </tr>
            <tr v-if="historyCityId === city.cityId" :id="`import-history-${city.cityId}`">
              <td colspan="6" class="bg-light p-3">
                <ListingImportHistory :city-id="city.cityId" :city-name="city.name" :refresh-key="checkedAt" />
              </td>
            </tr>
          </template>
          <tr v-if="!filteredMunicipalities.length">
            <td colspan="6" class="text-center text-muted py-4">
              {{ municipalities.length ? 'No municipalities match your search.' : 'No municipalities configured.' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </AdminComponent>
</template>

<script>
import { fetchApi } from '@/api';
import { Session } from '@/models/User';
import AdminComponent from '@/components/AdminComponent.vue';
import ListingImportHistory from '@/components/ListingImportHistory.vue';

export default {
  components: { AdminComponent, ListingImportHistory },
  data() {
    return { municipalities: [], search: '', loading: false, error: '', checkedAt: null, requestId: 0, historyCityId: null };
  },
  computed: {
    canView() {
      return Session.currentUserChecked.value && Session.currentUser.value?.role === 'admin';
    },
    filteredMunicipalities() {
      const query = this.search.trim().toLocaleLowerCase();
      return this.municipalities.filter(city => `${city.name} ${city.state}`.toLocaleLowerCase().includes(query));
    }
  },
  watch: {
    canView: {
      immediate: true,
      handler(allowed) {
        if (allowed) {
          this.loadImports();
        } else {
          this.requestId++;
          this.municipalities = [];
          this.checkedAt = null;
          this.error = '';
          this.loading = false;
          this.historyCityId = null;
        }
      }
    }
  },
  beforeUnmount() {
    this.requestId++;
  },
  methods: {
    async loadImports() {
      if (!this.canView || this.loading) return;
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = '';
      try {
        const result = await fetchApi('/v1/admin/listing-imports');
        if (requestId !== this.requestId) return;
        this.municipalities = result.municipalities;
        this.checkedAt = result.checkedAt;
      } catch {
        if (requestId !== this.requestId) return;
        this.error = this.checkedAt
          ? 'Could not refresh listing imports. Showing the last loaded results. Try Refresh again.'
          : 'Could not load listing imports. Try Refresh again.';
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    displayDate(value) {
      return new Date(value).toLocaleString();
    },
    displayCount(value) {
      return Number.isInteger(value) ? value.toLocaleString() : '—';
    },
    importDescription(result) {
      const methods = { provider_extract: 'Listing extract', discovery: 'Listing discovery' };
      const sources = { airbnb: 'Airbnb', vrbo: 'Vrbo', booking: 'Booking.com' };
      const label = methods[result.method] || 'Listing import';
      const sourceNames = result.sources.map(source => sources[source] || source);
      return sourceNames.length ? `${label} · ${sourceNames.join(', ')}` : label;
    }
  }
};
</script>

<style scoped>
.import-count { font-variant-numeric: tabular-nums; }
</style>
